// UnitSelectionTray — bottom panel showing the selected unit plus nearby
// friendly units. Each card shows HP, MP, and hero abilities. Click to
// switch selection. Only shown when a unit is selected.

import type { SceneNode } from '@aod/protocol';
import { useMemo } from 'react';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import type { Selection } from '../testBridge/useTestBridge.js';

const NEARBY_RADIUS = 3.0; // world-space units (~3 hexes)

interface UnitSelectionTrayProps {
  readonly selected: SceneNode;
  readonly allUnits: ReadonlyArray<SceneNode>;
  readonly localPlayerIndex: number;
  readonly onSelectUnit: (unitId: string) => void;
}

interface UnitCard {
  node: SceneNode;
  hp: number;
  hpMax: number;
  mp: number;
  mpMax: number;
  kind: string;
  isSelected: boolean;
  hero?: {
    name: string | null;
    level: number;
    abilities: Array<{ abilityId: string | null; cooldown: number }>;
  };
}

function parseUnitCard(node: SceneNode, isSelected: boolean): UnitCard {
  const hp = (node.props?.hp as number) ?? 0;
  const hpMax = (node.props?.hpMax as number) ?? 1;
  const mp = (node.props?.remainingMovement as number) ?? 0;
  const mpMax = (node.props?.maxMovement as number) ?? 1;
  const kind = (node.props?.kind as string) ?? 'unit';
  const heroProps = node.props?.hero as Record<string, unknown> | undefined;

  let hero: UnitCard['hero'] | undefined;
  if (heroProps) {
    const abilities =
      (heroProps.abilitySlots as Array<{ abilityId: string | null; cooldown: number }>) ?? [];
    hero = {
      name: (heroProps.name as string | undefined) ?? null,
      level: (heroProps.level as number) ?? 1,
      abilities,
    };
  }

  return { node, hp, hpMax, mp, mpMax, kind, isSelected, hero };
}

export function UnitSelectionTray({
  selected,
  allUnits,
  localPlayerIndex,
  onSelectUnit,
}: UnitSelectionTrayProps) {
  const t = useT();
  const selectedPos = selected.transform.position as [number, number, number];
  const [sx, , sz] = selectedPos;

  const cards = useMemo(() => {
    const result: UnitCard[] = [];

    // Selected unit first.
    result.push(parseUnitCard(selected, true));

    // Nearby friendly units.
    for (const u of allUnits) {
      if (u.id === selected.id) continue;
      if ((u.props?.ownerId as number | undefined) !== localPlayerIndex) continue;
      const [ux, , uz] = u.transform.position as [number, number, number];
      const dx = ux - sx;
      const dz = uz - sz;
      if (Math.sqrt(dx * dx + dz * dz) <= NEARBY_RADIUS) {
        result.push(parseUnitCard(u, false));
      }
    }

    return result;
  }, [selected, allUnits, localPlayerIndex, sx, sz]);

  if (cards.length === 0) return null;

  return (
    <div
      data-testid="unit-tray"
      {...appDom({ id: 'unit-tray', type: 'Container', scope: 'main', label: t('unitTray.label') })}
      className="pointer-events-none absolute inset-x-2 bottom-[6.75rem] z-20 flex justify-center md:bottom-2"
    >
      <div
        data-coachmark-target="first-scout"
        className="pointer-events-auto flex max-w-full gap-1.5 overflow-x-auto rounded-lg border border-border/70 bg-panel-scrim p-2 text-card-foreground shadow-lg backdrop-blur"
      >
        {cards.map((card) => (
          <UnitCard key={card.node.id} card={card} onClick={() => onSelectUnit(card.node.id)} />
        ))}
      </div>
    </div>
  );
}

function UnitCard({ card, onClick }: { card: UnitCard; onClick: () => void }) {
  const t = useT();
  const { hp, hpMax, mp, mpMax, kind, isSelected, hero } = card;
  const hpPct = hpMax > 0 ? Math.max(0, hp / hpMax) : 0;
  const mpPct = mpMax > 0 ? Math.max(0, mp / mpMax) : 0;
  const hpColor = hpPct > 0.5 ? '#44cc44' : hpPct > 0.25 ? '#ccaa44' : '#cc4444';

  return (
    <button
      type="button"
      className={`flex flex-col gap-1 rounded px-2.5 py-1.5 text-left text-[10px] leading-none transition-colors min-w-[80px] shrink-0
        ${isSelected ? 'bg-primary/15 ring-1 ring-primary/60' : 'bg-muted/60 hover:bg-muted'}`}
      onClick={onClick}
    >
      {/* Name / kind */}
      <div className="flex items-center gap-1">
        <span className="font-medium text-foreground text-[11px] truncate">
          {hero ? (hero.name ?? t('unitTray.hero')) : kind}
        </span>
        {hero && (
          <span className="text-[9px] text-amber-700 tabular-nums dark:text-amber-300">
            {t('unitTray.level', { level: hero.level })}
          </span>
        )}
      </div>

      {/* HP bar */}
      <div className="flex items-center gap-1">
        <span className="w-4 text-[9px] text-muted-foreground">{t('common.hp')}</span>
        <div className="h-1.5 flex-1 rounded-full bg-foreground/10">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${hpPct * 100}%`, backgroundColor: hpColor }}
          />
        </div>
        <span className="text-[9px] text-muted-foreground tabular-nums w-8 text-right">
          {hp}/{hpMax}
        </span>
      </div>

      {/* MP bar */}
      <div className="flex items-center gap-1">
        <span className="w-4 text-[9px] text-muted-foreground">{t('unitTray.mp')}</span>
        <div className="h-1.5 flex-1 rounded-full bg-foreground/10">
          <div
            className="h-full rounded-full bg-blue-500/80 transition-all dark:bg-blue-400/70"
            style={{ width: `${mpPct * 100}%` }}
          />
        </div>
        <span className="text-[9px] text-muted-foreground tabular-nums w-8 text-right">
          {mp}/{mpMax}
        </span>
      </div>

      {/* Hero abilities */}
      {hero && hero.abilities.length > 0 && (
        <div className="flex gap-0.5 mt-0.5 flex-wrap">
          {hero.abilities.slice(0, 4).map((a, i) => (
            <span
              key={a.abilityId ?? `empty-${i}`}
              className={`rounded px-1 py-0 text-[8px] ${
                a.cooldown > 0
                  ? 'bg-muted text-muted-foreground/70'
                  : 'bg-amber-400/20 text-amber-800 dark:text-amber-300'
              }`}
              title={a.abilityId ?? t('unitTray.emptySlot')}
            >
              {(a.abilityId ?? '—').slice(0, 6)}
              {a.cooldown > 0 ? ` (${a.cooldown})` : ''}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}
